import React, {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from "react";

import type { AxiosResponse } from "axios";
import axios from "axios";

import type { Population } from "~/src/types/__generated__/cc-api/api";

interface PopulationsState {
  data: Population[];
  loading: boolean;
  error: string | null;
  count: number;
  query: string;
}

interface PopulationBuilderContextType {
  populations: PopulationsState;
  loadPopulations: (queryParameters?: string) => void;
  addPopulation: (population: Population) => void;
  updatePopulation: (uuid: string, patch: Partial<Population>) => void;
  removePopulation: (uuid: string) => void;
}

const PopulationBuilderContext = createContext<
  PopulationBuilderContextType | undefined
>(undefined);

export { PopulationBuilderContext };

interface PopulationBuilderProviderProps {
  children: ReactNode;
}

export const PopulationBuilderProvider = ({
  children,
}: PopulationBuilderProviderProps) => {
  const [populations, setPopulations] = useState<PopulationsState>({
    data: [],
    loading: true,
    error: null,
    count: 0,
    query: "",
  });

  const loadPopulations = (queryParameters?: string) => {
    setPopulations((prev) => {
      return { ...prev, loading: true, error: null };
    });

    axios
      .get(
        `population/populations${queryParameters ? `${queryParameters}` : ""}`,
      )
      .then(
        (
          res: AxiosResponse<{
            result: { results: Population[]; count: number };
          }>,
        ) => {
          setPopulations({
            data: res.data.result.results ?? [],
            loading: false,
            error: null,
            count: res.data.result.count ?? 0,
            query: queryParameters ?? "",
          });
        },
      )
      .catch(() => {
        setPopulations({
          data: [],
          loading: false,
          error: "Failed to load populations",
          count: 0,
          query: queryParameters ?? "",
        });
      });
  };

  const addPopulation = (population: Population) => {
    setPopulations((prev) => {
      return {
        ...prev,
        data: [population, ...prev.data],
        count: prev.count + 1,
      };
    });
  };

  const updatePopulation = (uuid: string, patch: Partial<Population>) => {
    setPopulations((prev) => {
      return {
        ...prev,
        data: prev.data.map((p) => (p.uuid === uuid ? { ...p, ...patch } : p)),
      };
    });
  };

  const removePopulation = (uuid: string) => {
    setPopulations((prev) => {
      return {
        ...prev,
        data: prev.data.filter((p) => p.uuid !== uuid),
        count: Math.max(0, prev.count - 1),
      };
    });
  };

  return (
    <PopulationBuilderContext.Provider
      value={{
        populations,
        loadPopulations,
        addPopulation,
        updatePopulation,
        removePopulation,
      }}
    >
      {children}
    </PopulationBuilderContext.Provider>
  );
};

export const usePopulationBuilderContext = (): PopulationBuilderContextType => {
  const context = useContext(PopulationBuilderContext);
  if (!context) {
    throw new Error(
      "usePopulationBuilderContext must be used within a PopulationBuilderProvider",
    );
  }
  return context;
};
